<script setup>
import { computed, inject, ref, watch } from 'vue';
import Spinner from '@/components/Spinner.vue';
import ModalSimple from '@/components/ModalSimple.vue';
import apiPanelPerfilSalida from '@/api/cifra/lomloe/panel_perfil_salida';
import { fullname } from '@/libs/usuarios';

const I18n = inject('I18n');

const props = defineProps({
  idGrupo: { type: Number, required: true },
  // Realmente es obligatorio pero en el panel se carga solo un modal (sin idAlumno)
  // y luego se le asigna el idAlumno al abrirlo porque si no serían muchos modales y
  // ralentizaría la carga de la página mucho (y memoria)
  idAlumno: { type: Number, default: null },
});

const show = defineModel('show');

const emits = defineEmits(['cargado', 'cerrado']);

const cargando = ref(false);
const datosLomloe = ref();
const alumno = computed(() => datosLomloe.value?.alumno);
const leyEducativa = computed(() => datosLomloe.value?.ley_educativa);
const mostrarPerfilSalida = computed(() => leyEducativa.value?.lomloe);
const tituloCompetenciasRas = computed(() => {
  return I18n.t(leyEducativa.value?.looifp ? 'resultados_aprendizaje' : 'lomloe_competencias_especificas');
});

function loadData(data) {
  datosLomloe.value = data;
  emits('cargado', data);
}

function reset() {
  datosLomloe.value = null;
}

async function cargarDatos() {
  cargando.value = true;
  try {
    const result = await apiPanelPerfilSalida.calificacionesAlumno(
      props.idGrupo, props.idAlumno,
    );
    loadData(result.data);
  } catch (error) {
    console.error(error);
  } finally {
    cargando.value = false;
  }
}

watch(show, (visible) => {
  if (visible) {
    cargarDatos();
  } else {
    reset();
    emits('cerrado');
  }
});


const competenciasClave = computed(() => {
  // Las competencias y descriptores siempre son los mismos para todas las evaluaciones
  const evaluacion = datosLomloe.value?.evaluaciones?.[0];
  if (!evaluacion) {
    return [];
  }

  const competenciasMap = new Map();
  const competencias = evaluacion?.lomloe?.competencias_clave;
  if (competencias) {
    Object.values(competencias).forEach((competencia) => {
      if (competencia?.definicion && !competenciasMap.has(competencia.definicion.id)) {
        const competenciaInfo = Object.assign({}, competencia);
        competenciaInfo.descriptores = competencia.descriptores_operativos?.map(d => d.definicion) || [];
        competenciasMap.set(competencia.definicion.id, competenciaInfo);
      }
    });
  }

  // Ordenar las competencias por el campo "orden"
  return Array.from(competenciasMap.values()).sort((a, b) => a.definicion.orden - b.definicion.orden);
});

// Estado reactivo para controlar el despliegue de descriptores por competencia
const competenciasDesplegadas = ref({});
const competenciasEspecificasDesplegadas = ref({});

function toggleDescriptores(competenciaId) {
  competenciasDesplegadas.value[competenciaId] = !competenciasDesplegadas.value[competenciaId];
}

function toggleCriteriosEvaluacion(competenciaId) {
  competenciasEspecificasDesplegadas.value[competenciaId] = !competenciasEspecificasDesplegadas.value[competenciaId];
}

const evaluaciones = computed(() => {
  return datosLomloe.value?.evaluaciones?.map(e => e.evaluacion) || [];
});

const asignaturas = computed(() => {
  // Recopilar todas las asignaturas únicas de todas las evaluaciones
  const asignaturasMap = new Map();
  datosLomloe.value?.evaluaciones?.forEach(evaluacion => {
    evaluacion?.lomloe?.info_asignaturas?.forEach(info => {
      const asignatura = info.asignatura;
      if (asignatura && !asignaturasMap.has(asignatura.id)) {
        asignaturasMap.set(asignatura.id, asignatura);
      }
    });
  });

  // Ordenar alfabéticamente por nombre
  return Array.from(asignaturasMap.values()).sort((a, b) =>
    a.nombre.localeCompare(b.nombre)
  );
});

const asignaturaSeleccionada = ref(null);

// Cuando se cargan los datos, seleccionar la primera asignatura automáticamente
watch(asignaturas, (nuevasAsignaturas) => {
  if (nuevasAsignaturas.length > 0 && !asignaturaSeleccionada.value) {
    asignaturaSeleccionada.value = nuevasAsignaturas[0].id;
  }
}, { immediate: true });

const competenciasEspecificas = computed(() => {
  if (!asignaturaSeleccionada.value) {
    return [];
  }

  // Obtener las competencias de la primera evaluación (son las mismas para todas)
  const evaluacion = datosLomloe.value?.evaluaciones?.[0];
  if (!evaluacion) {
    return [];
  }

  const infoAsignatura = evaluacion?.lomloe?.info_asignaturas?.find(
    info => info.asignatura.id === asignaturaSeleccionada.value
  );

  if (!infoAsignatura?.competencias_especificas) {
    return [];
  }

  // Ordenar las competencias específicas por orden
  return infoAsignatura.competencias_especificas
    .map(ce => ({
      definicion: ce.definicion,
      criterios_evaluacion: ce.criterios_evaluacion || []
    }))
    .sort((a, b) => (a.definicion.orden || 0) - (b.definicion.orden || 0));
});

function obtenerInfoAsignatura(evaluacionId, asignaturaId) {
  const evaluacionLomloe = datosLomloe.value.evaluaciones.find(e => e.evaluacion.id === evaluacionId)?.lomloe;
  if (!evaluacionLomloe?.info_asignaturas) {
    return null;
  }
  return evaluacionLomloe.info_asignaturas.find(info => info.asignatura.id === asignaturaId) || null;
}

function calificacionCompetenciaEspecifica(evaluacionId, asignaturaId, competenciaId) {
  const infoAsignatura = obtenerInfoAsignatura(evaluacionId, asignaturaId);
  if (!infoAsignatura?.competencias_especificas) {
    return null;
  }
  const competencia = infoAsignatura.competencias_especificas.find(ce => ce.definicion.id === competenciaId);
  return competencia?.calificacion || null;
}

function calificacionCriterioEvaluacion(evaluacionId, asignaturaId, competenciaId, criterioId) {
  const infoAsignatura = obtenerInfoAsignatura(evaluacionId, asignaturaId);
  if (!infoAsignatura?.competencias_especificas) {
    return null;
  }
  const competencia = infoAsignatura.competencias_especificas.find(ce => ce.definicion.id === competenciaId);
  if (!competencia?.criterios_evaluacion) {
    return null;
  }
  const criterio = competencia.criterios_evaluacion.find(cri => cri.definicion.id === criterioId);
  return criterio?.calificacion || null;
}

function obtenerCompetenciaClave(evaluacionId, competenciaId) {
  const evaluacionLomloe = datosLomloe.value.evaluaciones.find(e => e.evaluacion.id === evaluacionId)?.lomloe;
  if (!evaluacionLomloe?.competencias_clave) {
    return null;
  }
  return evaluacionLomloe.competencias_clave.find(c => c.definicion.id === competenciaId) || null;
}

function calificacionCompetenciaClave(evaluacionId, competenciaId) {
  return obtenerCompetenciaClave(evaluacionId, competenciaId)?.calificacion || null;
}

function calificacionDescriptorOperativo(evaluacionId, competenciaId, descriptorId) {
  const competencia = obtenerCompetenciaClave(evaluacionId, competenciaId);
  if (!competencia?.descriptores_operativos) {
    return null;
  }
  return competencia.descriptores_operativos.find(d => d.definicion.id === descriptorId)?.calificacion || null;
}
</script>

<template>
  <teleport to="body">
    <ModalSimple
      :model-value="show"
      @update:modelValue="show = $event"
      :mostrar-boton-cerrar="true"
      class-modal="w-95"
    >
      <Spinner v-if="cargando" />
      <div v-if="!cargando && alumno">
        <thumbnail-header
          :imagen="alumno.foto_url"
          :titulo="fullname(alumno)"
          :subtitulo="$t('perfil_salida')"
          class="m-b border-bottom-cifra p-b-sm"
        />

        <ul class="nav nav-pills nav-pills-sm nav-pills-fill d-flex justify-content-center">
          <li v-if="mostrarPerfilSalida" class="nav-item" :class="{ active: mostrarPerfilSalida }">
            <a href="#tab-perfil-salida" class="nav-link" role="tab" data-toggle="tab">
              <i class="fa fa-book"></i> {{ $t('perfil_salida') }}
            </a>
          </li>
          <li class="nav-item" :class="{ active: !mostrarPerfilSalida }">
            <a href="#tab-competencias-o-ras" class="nav-link" role="tab" data-toggle="tab">
              <i class="fa fa-book"></i> {{ tituloCompetenciasRas }}
            </a>
          </li>
        </ul>
        <div class="tab-content mt-3">
          <div v-if="mostrarPerfilSalida" id="tab-perfil-salida" role="tabpanel" class="tab-pane fade in" :class="{ active: mostrarPerfilSalida }">
            <table class="table">
              <thead>
                <tr>
                  <th></th>
                  <th></th>
                  <th v-for="evaluacion in evaluaciones" :key="evaluacion.id" class="text-nowrap text-center">
                    {{ evaluacion.nombre }}
                  </th>
                </tr>
                <template v-for="competencia in competenciasClave" :key="competencia.id">
                  <tr class="bg-secondary competencia-row" style="cursor:pointer" @click="toggleDescriptores(competencia.definicion.id)">
                    <td class="text-bold text-nowrap align-middle">
                      <span>
                        <i :class="['fa', competenciasDesplegadas[competencia.definicion.id] ? 'fa-chevron-down' : 'fa-chevron-right']" style="margin-right: 6px;"></i>
                        {{ competencia.definicion.codigo }}
                      </span>
                    </td>
                    <td>
                       {{ competencia.definicion.nombre }}
                    </td>
                    <td v-for="evaluacion in evaluaciones" :key="evaluacion.id" class="text-nowrap text-center align-middle">
                      {{ calificacionCompetenciaClave(evaluacion.id, competencia.definicion.id)?.valor_to_human || '-' }}
                    </td>
                  </tr>
                  <tr v-for="descriptor in competencia.descriptores_operativos"
                      :key="descriptor.id"
                      v-show="competenciasDesplegadas[competencia.definicion.id]"
                  >
                    <td>
                      <span class="ps-3">
                        {{ descriptor.definicion.codigo }}
                      </span>
                    </td>
                    <td>
                      {{ descriptor.definicion.nombre }}
                    </td>
                    <td v-for="evaluacion in evaluaciones" :key="evaluacion.id" class="text-nowrap text-center align-middle">
                      {{ calificacionDescriptorOperativo(evaluacion.id, competencia.definicion.id, descriptor.definicion.id)?.valor_to_human || '-' }}
                    </td>
                  </tr>
                </template>
              </thead>
            </table>
          </div>
          <div id="tab-competencias-o-ras" role="tabpanel" class="tab-pane fade in" :class="{ active: !mostrarPerfilSalida }">
            <div v-if="asignaturas.length > 0" class="m-b">
              <label class="control-label">{{ $t('Asignatura') }}</label>
              <select v-model="asignaturaSeleccionada" class="form-control">
                <option v-for="asignatura in asignaturas" :key="asignatura.id" :value="asignatura.id">
                  {{ asignatura.nombre }}
                </option>
              </select>
            </div>
            <div v-else class="alert alert-info">
              {{ $t('asignaturas_no_encontradas') }}
            </div>

            <div v-if="asignaturaSeleccionada && competenciasEspecificas.length > 0" class="mt-3">
              <table class="table">
                <thead>
                  <tr>
                    <th></th>
                    <th></th>
                    <th v-for="evaluacion in evaluaciones" :key="evaluacion.id" class="text-nowrap text-center">
                      {{ evaluacion.nombre }}
                    </th>
                  </tr>
                  <template v-for="competencia in competenciasEspecificas" :key="competencia.definicion.id">
                    <tr class="bg-secondary competencia-row" style="cursor:pointer" @click="toggleCriteriosEvaluacion(competencia.definicion.id)">
                      <td class="text-bold text-nowrap align-middle">
                        <span>
                          <i :class="['fa', competenciasEspecificasDesplegadas[competencia.definicion.id] ? 'fa-chevron-down' : 'fa-chevron-right']" style="margin-right: 6px;"></i>
                          {{ competencia.definicion.codigo }}
                        </span>
                      </td>
                      <td>
                        {{ competencia.definicion.nombre }}
                      </td>
                      <td v-for="evaluacion in evaluaciones" :key="evaluacion.id" class="text-nowrap text-center align-middle">
                        {{ calificacionCompetenciaEspecifica(evaluacion.id, asignaturaSeleccionada, competencia.definicion.id)?.valor_to_human || '-' }}
                      </td>
                    </tr>
                    <tr v-for="criterio in competencia.criterios_evaluacion"
                        :key="criterio.definicion.id"
                        v-show="competenciasEspecificasDesplegadas[competencia.definicion.id]"
                    >
                      <td>
                        <span class="ps-3">
                          {{ criterio.definicion.codigo }}
                        </span>
                      </td>
                      <td>
                        {{ criterio.definicion.nombre }}
                      </td>
                      <td v-for="evaluacion in evaluaciones" :key="evaluacion.id" class="text-nowrap text-center align-middle">
                        {{ calificacionCriterioEvaluacion(evaluacion.id, asignaturaSeleccionada, competencia.definicion.id, criterio.definicion.id)?.valor_to_human || '-' }}
                      </td>
                    </tr>
                  </template>
                </thead>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ModalSimple>
  </teleport>
</template>

<style lang="scss" scoped>
.competencia-row:hover {
  background-color: #e0e0e0 !important;
}
</style>
